import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import moment from 'moment-timezone';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import { calculateShiftEndTime } from 'src/utils/shift';
import { showToastAction } from '../ui';
import {
    fetchRequests,
    requestsFetched,
    requestsFetchFailed,
    rejectRequest,
    rejectSucceeded,
    rejectFailed,
    approveRequest,
    approveSucceeded,
    approveFailed,
} from '../manualTimeRequest';

// Finds a contractor's tracker in a job's shifts (staffer may be populated).
const findContractorTracker = (job, stafferId) => {
    if (!job || !Array.isArray(job.staffersAndShifts)) {
        return null;
    }
    const target = String(stafferId);
    const shift = job.staffersAndShifts.find(
        s => s && s.staffer && String(s.staffer._id || s.staffer) === target,
    );
    return shift ? shift.tracker : null;
};

function* fetchRequestsEffect() {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.manualTimeRequests,
            method: 'GET',
        });
        yield put(requestsFetched(response));
    } catch (e) {
        yield put(requestsFetchFailed(e.message));
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to load manual time requests',
            }),
        );
    }
}

function* rejectRequestEffect(action) {
    const { id } = action.payload;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.approveOrRejectManualTimeRequest(id),
            method: 'POST',
            body: { isRequest: false, status: 'rejected' },
        });
        yield put(rejectSucceeded({ id }));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Request rejected successfully',
            }),
        );
    } catch (e) {
        yield put(rejectFailed(e.message));
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to reject request',
            }),
        );
    }
}

/**
 * Approves a manual time request, replicating Admin 1.0's flow exactly by
 * calling the same backend endpoints:
 *   - tracker exists  → PATCH /api/tracker/confirmLoggedHours/:staffer  (randomizes,
 *     applies breaks, sets hasLoggedHoursConfirm/manual times/fee, notifies staffer)
 *   - no tracker      → POST /api/tracker/createTracker/manually (+ invoice patch)
 * then POST /api/manualHoursLogged/approveOrDisapprove/:id { isRequest: true }
 * which flips the request to approved and pushes the staffer the "approved" note.
 */
function* approveRequestEffect(action) {
    const {
        request,
        contractorShift,
        timeValue,
        chargeManualHoursFee,
        manualHoursFeeWaiverReason,
        managerApproval,
    } = action.payload;

    try {
        const job = request.job;
        const stafferId = request.user._id;
        const tracker = contractorShift && contractorShift.tracker;
        const start = job.shiftStartTime;
        const end = calculateShiftEndTime(job.shiftStartTime, timeValue);

        // Manual-hours feature cutoff (matches Admin 1.0's featureEnableFromToday).
        const featureEnabled = request.created_date
            ? moment(request.created_date)
                  .tz('America/Toronto')
                  .isAfter(
                      moment('2018-12-19T02:12:41.592Z').tz('America/Toronto'),
                  )
            : false;

        if (featureEnabled && tracker && tracker._id) {
            // PATH A — the manual-hours confirmation endpoint.
            yield call(SendApiRequest, {
                url: apiRoutes.confirmLoggedHours(stafferId),
                method: 'PATCH',
                body: {
                    startAndEndTime: { start, end },
                    jobId: job._id,
                    timeZone: job.timeZone,
                    chargeManualHoursFee,
                    manualHoursFeeWaiverReason,
                    managerApproval,
                },
            });
        } else {
            // PATH B — create/update the tracker manually, then patch the invoice.
            const trackerId = tracker ? tracker._id : null;
            const trackerBody = trackerId
                ? {
                      trackerId,
                      startTime: start,
                      stopTime: end,
                      dontRandomizeTime: false,
                      hasManualHoursRequestConfirmed: true,
                      chargeManualHoursFee: chargeManualHoursFee || false,
                      manualHoursFeeWaiverReason: chargeManualHoursFee
                          ? ''
                          : manualHoursFeeWaiverReason,
                      managerApproval: chargeManualHoursFee
                          ? false
                          : managerApproval,
                  }
                : {
                      jobId: job._id,
                      userId: stafferId,
                      startTime: start,
                      stopTime: end,
                      hasManualHoursRequestConfirmed: true,
                      chargeManualHoursFee: chargeManualHoursFee || false,
                      manualHoursFeeWaiverReason: chargeManualHoursFee
                          ? ''
                          : manualHoursFeeWaiverReason,
                      managerApproval: chargeManualHoursFee
                          ? false
                          : managerApproval,
                  };

            const res = yield call(SendApiRequest, {
                url: trackerId
                    ? apiRoutes.updateTrackerV1
                    : apiRoutes.createTrackerManually,
                method: trackerId ? 'PATCH' : 'POST',
                body: trackerBody,
            });

            let invoiceId =
                (contractorShift.invoices && contractorShift.invoices[0]) ||
                job.invoice;
            if (invoiceId && typeof invoiceId === 'object') {
                invoiceId = invoiceId._id;
            }
            if (res && res.invoice) {
                invoiceId = res.invoice._id;
            }
            if (!invoiceId && res && res.job) {
                invoiceId =
                    res.job.isABroadcastRequest &&
                    res.job.isABroadcastRequest.isTrue
                        ? res.job.invoice._id
                        : res.job.staffersAndShifts[0].invoices[0]._id;
            }

            if (invoiceId) {
                const resultTracker =
                    res && res.tracker && res.tracker.start
                        ? res.tracker
                        : findContractorTracker(res && res.job, stafferId);
                yield call(SendApiRequest, {
                    url: apiRoutes.invoicesNewModel(invoiceId),
                    method: 'PATCH',
                    body: {
                        tracker: resultTracker,
                        stafferId:
                            resultTracker &&
                            (typeof resultTracker.user === 'object'
                                ? resultTracker.user._id
                                : resultTracker.user),
                        isEditTracker: true,
                        jobId: (res && res.job && res.job._id) || job._id,
                        isSendEmail: false,
                        startEditingInvoice: job.startEditingInvoice,
                        calledFromManualHoursLoggedView: true,
                    },
                });
            }
        }

        // Flip the request to approved + push the staffer the approval notice.
        yield call(SendApiRequest, {
            url: apiRoutes.approveManualHoursRequest(request._id),
            method: 'POST',
            body: { isRequest: true, status: 'approved' },
        });

        // Drop the row immediately (like reject) rather than relying on a
        // refetch, which can fail intermittently when the DB is slow.
        yield put(approveSucceeded({ id: request._id }));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Tracker Edit Successfully',
            }),
        );
    } catch (e) {
        yield put(approveFailed(e.message));
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to edit tracker',
            }),
        );
    }
}

export const manualTimeRequestSagas = [
    takeLatest(fetchRequests.type, fetchRequestsEffect),
    takeEvery(rejectRequest.type, rejectRequestEffect),
    takeEvery(approveRequest.type, approveRequestEffect),
];
